import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSelectedSubMenu } from '../application/reducers/auth';
import { FiChevronDown } from 'react-icons/fi';
import './Submenu.scss';

const SubMenu = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const menuRef = useRef(null);
  const itemRefs = useRef({});
  const dropdownRef = useRef(null);
  const moreButtonRef = useRef(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hiddenMenus, setHiddenMenus] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const { selectedMenu, selectedSubMenu } = useSelector(state => state.auth);
  const { sidebar } = useSelector(state => state.ui);
  const { sub_menus = [], url = '' } = selectedMenu || {};

  const subMenuId = selectedSubMenu?.sub_menu_id || null;

  // Check overflow and detect which items are currently not visible
  const checkOverflow = useCallback(() => {
    if (!menuRef.current) return;
    const container = menuRef.current;
    const overflowing = container.scrollWidth > container.clientWidth;
    setIsOverflowing(overflowing);

    if (overflowing) {
      const containerRect = container.getBoundingClientRect();
      const hidden = sub_menus.filter(menu => {
        const el = itemRefs.current[menu.sub_menu_id];
        if (!el) return false;
        const elRect = el.getBoundingClientRect();
        // Item is not fully visible if it overflows left or right edge of container
        return elRect.right > containerRect.right + 1 || elRect.left < containerRect.left - 1;
      });
      setHiddenMenus(hidden);
    } else {
      setHiddenMenus([]);
      setShowDropdown(false);
    }
  }, [sub_menus]);

  // Run overflow check on mount, resize, and scroll within the tab bar
  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    const container = menuRef.current;
    if (container) container.addEventListener('scroll', checkOverflow);
    return () => {
      window.removeEventListener('resize', checkOverflow);
      if (container) container.removeEventListener('scroll', checkOverflow);
    };
  }, [checkOverflow]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        moreButtonRef.current && !moreButtonRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showDropdown]);

  // Keyboard navigation for submenu
  const handleKeyDown = useCallback((e) => {
    if (!sub_menus.length) return;
    const currentIdx = sub_menus.findIndex(m => m.sub_menu_id === subMenuId);
    if (e.key === 'ArrowRight') {
      const nextIdx = (currentIdx + 1) % sub_menus.length;
      dispatch(setSelectedSubMenu(sub_menus[nextIdx]));
      menuRef.current?.querySelectorAll('.sub-menu-item a')[nextIdx]?.focus();
      e.preventDefault();
    } else if (e.key === 'ArrowLeft') {
      const prevIdx = (currentIdx - 1 + sub_menus.length) % sub_menus.length;
      dispatch(setSelectedSubMenu(sub_menus[prevIdx]));
      menuRef.current?.querySelectorAll('.sub-menu-item a')[prevIdx]?.focus();
      e.preventDefault();
    }
  }, [sub_menus, subMenuId, dispatch]);

  // Auto-scroll active item into view
  useEffect(() => {
    if (menuRef.current && selectedSubMenu) {
      const activeItem = menuRef.current.querySelector('.sub-menu-item a.active');
      if (activeItem) {
        activeItem.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }
  }, [selectedSubMenu]);

  // Auto-select submenu based on current route
  useEffect(() => {
    if (sub_menus.length > 0 && !selectedSubMenu) {
      const currentPath = location.pathname;
      const matchingMenu = sub_menus.find(menu => 
        currentPath.includes(`${url}/${menu.url}`)
      );
      
      if (matchingMenu) {
        dispatch(setSelectedSubMenu(matchingMenu));
      } else {
        // Default to first submenu if no match
        dispatch(setSelectedSubMenu(sub_menus[0]));
      }
    }
  }, [location.pathname, sub_menus, selectedSubMenu, url, dispatch]);

  // Memoize sub-menu items to avoid re-rendering; attach itemRefs for overflow detection
  const subMenuItems = useMemo(() =>
    sub_menus.map((menu, index) => {
      const isActive = subMenuId === menu.sub_menu_id;
      const linkTo = `${url}/${menu.url || '#'}`;
      return (
        <div
          className='sub-menu-item'
          key={menu.sub_menu_id || index}
          ref={el => { itemRefs.current[menu.sub_menu_id] = el; }}
        >
          <Link
            onClick={() => {
              dispatch(setSelectedSubMenu(menu));
            }}
            className={isActive ? 'active' : ''}
            to={linkTo}
            title={menu.name}
            aria-current={isActive ? 'page' : undefined}
            tabIndex={0}
            role="menuitem"
            onKeyDown={handleKeyDown}
          >
            {menu.icon && (
              <span className="menu-icon" aria-hidden="true">
                {menu.icon}
              </span>
            )}
            <span className="menu-text">{menu.name}</span>
          </Link>
        </div>
      );
    })
    , [subMenuId, sub_menus, dispatch, url, handleKeyDown]);

  // Don't render if no sub-menus
  if (sub_menus.length === 0) {
    return null;
  }

  const hasHidden = hiddenMenus.length > 0;

  return (
    <div 
      className={`sub-menu-container${sidebar === 'close' ? ' sidebar-collapsed' : ''}${hasHidden ? ' has-overflow' : ''}`}
      role="navigation"
      aria-label="Sub navigation"
    >
      <div 
        className='sub-menu' 
        ref={menuRef}
        role="menubar"
        tabIndex={0}
        aria-orientation="horizontal"
      >
        {subMenuItems}
      </div>

      {/* More dropdown button — only shown when items overflow */}
      {hasHidden && (
        <div className="sub-menu-more-wrap">
          <button
            ref={moreButtonRef}
            className={`sub-menu-more-btn${showDropdown ? ' open' : ''}`}
            onClick={() => setShowDropdown(v => !v)}
            aria-haspopup="true"
            aria-expanded={showDropdown}
            aria-label={`${hiddenMenus.length} more navigation items`}
            title="More"
          >
            More
            <span className="more-count">{hiddenMenus.length}</span>
            <FiChevronDown size={13} />
          </button>

          {showDropdown && (
            <div
              className="sub-menu-more-dropdown"
              ref={dropdownRef}
              role="menu"
              aria-label="Hidden navigation items"
            >
              <div className="dropdown-label">More</div>
              {hiddenMenus.map((menu) => {
                const isActive = subMenuId === menu.sub_menu_id;
                const linkTo = `${url}/${menu.url || '#'}`;
                return (
                  <Link
                    key={menu.sub_menu_id}
                    className={`sub-menu-more-item${isActive ? ' active' : ''}`}
                    to={linkTo}
                    role="menuitem"
                    onClick={() => {
                      dispatch(setSelectedSubMenu(menu));
                      setShowDropdown(false);
                    }}
                  >
                    {menu.icon && (
                      <span className="menu-icon" aria-hidden="true">{menu.icon}</span>
                    )}
                    <span className="menu-text">{menu.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubMenu;
